import { ProgramModel } from '@24i/st-api-connector';
import { mergeRanges, mergePrograms, startExpirationTimer, makeExpirationDate } from './helpers';

const getChannels = (channelMap, params) => {
    if (typeof channelMap.load !== 'function') return undefined;
    const { list = 'all', limit = 20, offset = 0 } = params;
    const channels = channelMap.load(list, false);
    if (!channels) {
        return undefined;
    }
    return channels.items.map(({ channelId }) => channelId).slice(offset, limit + offset);
};

const getProgramSlice = (programs, from, to) =>
    programs
        .filter(({ start, end }) => end >= from && start <= to)
        .map((program) => new ProgramModel(program));

const isRangeCached = (ranges, from, to) =>
    ranges.some((range) => range.from <= from && range.to >= to);

const isChannelCached = (cache, from, to) => (channelId) => {
    const cachedChannel = cache[channelId];
    if (!cachedChannel) {
        return false;
    }
    const { range } = cachedChannel;
    return isRangeCached(range, from, to);
};

const parseChannelIdsParam = (channelIds, channelMap, params) =>
    channelIds ? channelIds.split(',') : getChannels(channelMap, params);

export class EPGCache {
    constructor(id, onUpdate, initialCache, channelMap) {
        this.id = id;
        this.onUpdate = onUpdate;
        const maxExpire = Object.keys(channelMap.cache).reduce(
            (maxCache, cacheItem) =>
                channelMap.cache[cacheItem].expiresIn > maxCache
                    ? channelMap.cache[cacheItem].expiresIn
                    : maxCache,
            0
        );
        this.cache = { expiresIn: makeExpirationDate(maxExpire), ...initialCache };
        this.channelMap = channelMap;
        startExpirationTimer(this.wipeCache);
    }

    checkExpiration() {
        function roughSizeOfObject(obj) {
            const str = JSON.stringify(obj);
            return new TextEncoder().encode(str).length;
        }

        const { expiresIn } = this.cache;
        if (expiresIn < Date.now() || roughSizeOfObject(this.cache) > 3145728) {
            this.wipeCache();
            return;
        }

        const now = Date.now();
        const cutoff = now - 3 * 60 * 60 * 1000;

        for (const [id, ch] of Object.entries(this.cache)) {
            if (id === 'expiresIn') continue;

            const isEmpty = !ch.programs || ch.programs.length === 0;
            const isStale = Array.isArray(ch.range) && ch.range.every((r) => r.to < cutoff);

            if (isEmpty || isStale) {
                delete this.cache[id];
            }
        }
    }

    wipeCache = async () => {
        this.cache = { expiresIn: makeExpirationDate() };
        this.channelMap = {};
        await this.onUpdate(this.id, true);
    };

    async save({ items, totalCount } = {}, params) {
        this.checkExpiration();
        if (!params || !items) return;
        const { from, to, channelIds } = params;
        const channels = parseChannelIdsParam(channelIds, this.channelMap, params) || [];

        channels.forEach((channelId) => {
            const { channel, programs = [] } =
                items.find((item) => item.channel.channelId === channelId) || {};
            const cachedChannel = this.cache[channelId] || {};
            const programsData = programs.map((program) => (program.data ? program.data : program));
            const mergedPrograms = mergePrograms(cachedChannel.programs, programsData);

            if (mergedPrograms.length === 0) return;

            this.cache[channelId] = {
                range: mergeRanges(cachedChannel.range, { from, to }),
                channel: channel || cachedChannel.channel,
                programs: mergedPrograms
            };
            if (totalCount) this.cache.totalCount = totalCount;
        });

        await this.onUpdate(this.id);
    }

    load(params) {
        const { channelIds, from, to } = params;
        const channels = parseChannelIdsParam(channelIds, this.channelMap, params);
        if (!channels || !channels.every(isChannelCached(this.cache, from, to))) {
            return null;
        }
        const items = channels.map((id) => {
            const { channel, programs } = this.cache[id];
            return {
                channel,
                programs: getProgramSlice(programs, from, to)
            };
        });
        const totalCount = channelIds ? items.length : this.cache.totalCount;
        return items && items.length > 0 ? { items, totalCount } : null;
    }
}
